import React, { useEffect, useState } from "react";
import { useSnackbar } from "@components";
import { Modal, Tooltip, Button, Segmented, ConfigProvider } from "antd";
import { Box, Grid, Typography } from "@mui/material";
import type { Dayjs } from "dayjs";
import { workflowContainerFullById } from "../../../../../src/libs/apis";
import { WorkflowContainerFullI } from "../../../../../src/libs/interfaces";
import {
  FundViewOutlined,
  ProfileOutlined,
  PictureOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { COLOR_PRIMARY, COLOR_SECONDARY } from "../../../../../src/libs/constants";
import { OneCard, OneVideo } from "../../../../../src/libs/shared";

const noShow = [
  "id",
  "client",
  "producer",
  "farm",
  "zone",
  "balerCode",
  "box",
  "brand",
  "container",
  "clientId",
  "producerId",
  "farmId",
  "magap",
  "boxId",
  "brandId",
  "closeProcess",
  "endLoading",
  "containerClose",
  "containerOulet",
  "numCajas",
  "cajasEstiba",
  "videoCajas",
  "videoCierreContenedor",
  "videoPanelExterior",
  "observation",
  "gancho",
  "ganchoCi",
  "calidad",
  "calidadCi",
  "date",
  "driver",
  "companyTransport",
  "dateVideoCajas",
  "dateVideoCierreContenedor",
  "dateVideoPanelExterior",
  "numKit",
  "placa",
  "timeStampSave",
  "magapOptional",
  "numCajasOptional",
  "cajasEstibaOptional",
  "boxOptional",
  "brandOptional",
  "boxOptionalId",
  "brandOptionalId",
  "producerOptional",
  "producerOptionalId",
  "farmOptional",
  "farmOptionalId",
  "farmOptionalZone",
  "balerCodeOptional",
];

const titles = {
  adhesivo: "Etiqueta Puerta Verificadora",
  adhesivoBisagra: "Etiqueta Bisagra",
  adhesivoPuerta: "Etiqueta Puerta Exportador",
  botellaExpor: "Sello Botella Exportador",
  botellaNaviera: "Sello Botella Naviera",
  botellaVerificadoraRastreo: "Sello Botella Verif.",
  cableExpor: "Sello Cable Exportador",
  cableVerificadora: "Sello Cable Verif.",
  evaluacionFruta: "Evaluación de Fruta",
  guiaTransporte: "Guía de Transporte",
  numRastreoSatelital: "Número de Rastreo",
  tarjaCarga: "Tarja de Carga",
  termografo: "Termógrafo",
  numKit: "Número de Kit",
  placa: "Placa Vehicular",
  ventoleraIzquierdaPhoto: "Ventolera Izquierda",
  ventoleraDerechaPhoto: "Ventolera Derecha",
  panelIzquierdoPhoto: "Foto de Panel Izquierdo",
  panelDerechoPhoto: "Foto de Panel Derecho",
} as any;

interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
  showAlt?: boolean;
}

interface OneCardI {
  title: string;
  photos: OnePhotoI[];
  value?: string;
  noLine?: boolean;
}

interface ShowContainerPropsI {
  id: number;
  disabled?: boolean; // Añadir propiedad para deshabilitar en modo offline
}

export const ShowContainer = ({ id }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) {
      setOpen(true);
    }
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };

  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer open={open} onClose={handleClose} id={id} />
      )}
    </>
  );
};

interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
}

export const ModalContainer = ({ open, onClose, id }: ModalContainerPropsI) => {
  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState<WorkflowContainerFullI>({
    container: "",
  } as WorkflowContainerFullI);
  const [cards, setCards] = useState<OneCardI[]>([]);
  const [cardVideos, setCardVideos] = useState<OnePhotoI[]>([]);
  const [lasFotos, setLasFotos] = useState<string[]>([]);
  const [tab, setTab] = useState<string | number>("info");
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    handleGetData();
    return () => {
      setLoading(true);
      setData({
        container: "",
      } as WorkflowContainerFullI);
      setCards([]);
    };
  }, []);

  const handleGetData = async () => {
    try {
      const { data: dataContainer } = await workflowContainerFullById(id);
      if (!dataContainer) return;
      setData(dataContainer);

      const arrPhotosTotal: string[] = [];
      const arrKeys = Object.keys(dataContainer).filter(
        (el) => !noShow.includes(el)
      ) as Array<keyof WorkflowContainerFullI>;

      const keysParents = arrKeys
        .filter((el) => !el.includes("Photo"))
        .filter((el) => !el.includes("photo"))
        .filter((el) => !el.includes("video"));

      const arrCards = [];
      for (const x of keysParents) {
        const obj: OneCardI = { title: titles[x] ?? x } as OneCardI;
        const arrPhotos = [];
        for (const y of arrKeys) {
          if (y !== x && y.includes(x)) {
            arrPhotos.push({
              src: dataContainer[y],
              alt: y,
            });
            arrPhotosTotal.push(dataContainer[y] as any);
          }
        }
        obj.photos = arrPhotos;
        obj.value = dataContainer[x] as string;
        arrCards.push(obj);
      }
      const objContainer = {
        title: "Contenedor",
        photos: [{ src: dataContainer["siglasFPhoto"], alt: "Contenedor" }],
      } as OneCardI;

      arrCards.unshift(objContainer);
      arrPhotosTotal.push(dataContainer["siglasFPhoto"] as any);

      const objCajasCon = {
        title: "Cajas Consolidadas",
        photos: [
          {
            src: dataContainer["cajasConsolidadasPhoto1"],
            alt: "cajasConsolidadasPhoto1",
          },
          {
            src: dataContainer["cajasConsolidadasPhoto2"],
            alt: "cajasConsolidadasPhoto2",
          },
          {
            src: dataContainer["cajasConsolidadasPhoto3"],
            alt: "cajasConsolidadasPhoto3",
          },
          {
            src: dataContainer["cajasConsolidadasPhoto4"],
            alt: "cajasConsolidadasPhoto4",
          },
          {
            src: dataContainer["cajasConsolidadasPhoto5"],
            alt: "cajasConsolidadasPhoto5",
          },
        ],
      };
      arrCards.push(objCajasCon);
      arrPhotosTotal.push(dataContainer["cajasConsolidadasPhoto1"] as any);
      arrPhotosTotal.push(dataContainer["cajasConsolidadasPhoto2"] as any);
      arrPhotosTotal.push(dataContainer["cajasConsolidadasPhoto3"] as any);
      arrPhotosTotal.push(dataContainer["cajasConsolidadasPhoto4"] as any);
      arrPhotosTotal.push(dataContainer["cajasConsolidadasPhoto5"] as any);

      const objCierreCon = {
        title: "Cierre de Contenedor",
        photos: [
          {
            src: dataContainer["cierreContenedorPhoto1"],
            alt: "cierreContenedorPhoto1",
          },
          {
            src: dataContainer["cierreContenedorPhoto2"],
            alt: "cierreContenedorPhoto2",
          },
          {
            src: dataContainer["cierreContenedorPhoto3"],
            alt: "cierreContenedorPhoto3",
          },
        ],
      };
      arrCards.push(objCierreCon);
      arrPhotosTotal.push(dataContainer["cierreContenedorPhoto1"] as any);
      arrPhotosTotal.push(dataContainer["cierreContenedorPhoto2"] as any);
      arrPhotosTotal.push(dataContainer["cierreContenedorPhoto3"] as any);

      const arrVideos = [
        { src: dataContainer.videoCajas, alt: "Video Cajas" },
        {
          src: dataContainer.videoCierreContenedor,
          alt: "Video Cierre Contenedor",
        },
        // { src: dataContainer.videoPanelExterior, alt: "Video Panel Exterior" },
      ];

      setCardVideos(arrVideos);
      setCards(arrCards);
      setLasFotos(arrPhotosTotal);
      setLoading(false);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
      setLoading(false);
    }
  };

  const d = data as any;
  const sections: { title: string; fields: [string, any][] }[] = [
    {
      title: "Datos generales",
      fields: [
        ["N° Contenedor", d.container],
        ["Exportador", d.client],
        ["MAGAP", d.magap],
        ["Productor", d.producer],
        ["Hacienda", d.farm],
        ["Zona", d.zone],
        ["Código empacadora", d.balerCode],
        ["Marca", d.brand],
        ["Tipo de caja", d.box],
        ["Número kit", d.numKit],
        ["N° cajas", d.numCajas],
        ["Cajas por estiba", d.cajasEstiba],
      ],
    },
    {
      title: "Datos opcionales (consolidado)",
      fields: [
        ["MAGAP", d.magapOptional],
        ["Productor", d.producerOptional],
        ["Hacienda", d.farmOptional],
        ["Zona", d.farmOptionalZone],
        ["Código empacadora", d.balerCodeOptional],
        ["Marca", d.brandOptional],
        ["Tipo de caja", d.boxOptional],
        ["N° cajas", d.numCajasOptional],
        ["Cajas por estiba", d.cajasEstibaOptional],
      ],
    },
    {
      title: "Transporte",
      fields: [
        ["Conductor", d.driver],
        ["Placa", d.placa],
        ["Compañía de transporte", d.companyTransport],
      ],
    },
    {
      title: "Calidad - Gancho",
      fields: [
        ["Calidad", d.calidad],
        ["Identificación calidad", d.calidadCi],
        ["Gancho", d.gancho],
        ["Identificación gancho", d.ganchoCi],
      ],
    },
    {
      title: "Sellos y documentos",
      fields: [
        "adhesivo",
        "adhesivoBisagra",
        "adhesivoPuerta",
        "botellaExpor",
        "botellaNaviera",
        "botellaVerificadoraRastreo",
        "cableExpor",
        "cableVerificadora",
        "numRastreoSatelital",
        "guiaTransporte",
        "tarjaCarga",
        "termografo",
        "evaluacionFruta",
      ].map((k) => [titles[k] ?? k, d[k]] as [string, any]),
    },
    {
      title: "Horas de proceso",
      fields: [
        ["Cierre de proceso", d.closeProcess],
        ["Fin de carga", d.endLoading],
        ["Cierre del contenedor", d.containerClose],
        ["Salida del contenedor", d.containerOulet],
      ],
    },
  ];

  const photosCount = cards.reduce(
    (acc, c) => acc + c.photos.filter((p) => p.src).length,
    0
  );

  return (
    <Modal
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "white" }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,.15)",
              fontSize: 18,
            }}
          >
            <FundViewOutlined />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
              Contenedor Full {data.container ? `N° ${data.container}` : ""}
            </Typography>
            <Typography sx={{ fontSize: 12.5, opacity: 0.85 }}>
              {d.client || "Información, fotos y videos del registro"}
            </Typography>
          </Box>
        </Box>
      }
      footer={null}
      loading={isLoading}
      open={open}
      onCancel={onClose}
      width={900}
      centered={true}
      zIndex={1602}
      destroyOnClose={true}
      closeIcon={<span style={{ color: "white" }}>✕</span>}
      styles={{
        header: {
          background: COLOR_PRIMARY,
          margin: 0,
          padding: "16px 24px",
          borderRadius: "8px 8px 0 0",
        },
        content: { padding: 0, borderRadius: 8, overflow: "hidden" },
        body: { padding: 0 },
      }}
    >
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e6ebf2" }}>
        <ConfigProvider
          theme={{
            components: {
              Segmented: {
                itemSelectedBg: COLOR_PRIMARY,
                itemSelectedColor: "#fff",
                itemColor: COLOR_PRIMARY,
                trackBg: "#e6ebf2",
              },
            },
          }}
        >
          <Segmented
            block
            size="large"
            value={tab}
            onChange={setTab}
            options={[
              { value: "info", icon: <ProfileOutlined />, label: "Información" },
              { value: "photos", icon: <PictureOutlined />, label: `Fotos (${photosCount})` },
              { value: "videos", icon: <VideoCameraOutlined />, label: "Videos" },
            ]}
          />
        </ConfigProvider>
      </Box>

      <Box sx={{ height: 520, overflowY: "auto", p: 2.5 }}>
        {tab === "info" && (
          <Grid container spacing={2.5}>
            {sections
              .filter((sec) => !sec.title.includes("opcionales") || sec.fields.some(([, v]) => v))
              .map((sec) => (
              <Grid item xs={12} key={sec.title}>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    textTransform: "uppercase",
                    color: COLOR_SECONDARY,
                    pb: 0.75,
                    mb: 1.5,
                    borderBottom: "1px solid #e6ebf2",
                  }}
                >
                  {sec.title}
                </Typography>
                <Grid container spacing={2}>
                  {sec.fields.map(([label, value]) => (
                    <Grid item xs={12} sm={6} md={4} key={`${sec.title}-${label}`}>
                      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: 14.5, fontWeight: 500, wordBreak: "break-word" }}>
                        {value || "—"}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  color: COLOR_SECONDARY,
                  pb: 0.75,
                  mb: 1.5,
                  borderBottom: "1px solid #e6ebf2",
                }}
              >
                Observación
              </Typography>
              <Typography sx={{ fontSize: 14.5, whiteSpace: "pre-wrap" }}>
                {d.observation || "—"}
              </Typography>
            </Grid>
          </Grid>
        )}

        {tab === "photos" && (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              width: "100%",
            }}
          >
            {cards.map((el) => (
              <OneCard data={el} key={`${el.title}-card`} lasFotos={lasFotos} />
            ))}
          </Box>
        )}

        {tab === "videos" && (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              width: "100%",
            }}
          >
            {cardVideos.map((el) => (
              <OneVideo {...el} key={`${el.alt}-video`} />
            ))}
          </Box>
        )}
      </Box>
    </Modal>
  );
};
